import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  CheckCircle,
  X,
  MapPin,
  Phone,
  Clock,
  Navigation,
  Users,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Mail,
  Building2,
  Truck,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import hospitalService from "../../services/hospitalService";
import { bookingService, authService } from "../../services";

const SuccessModal = ({
  isOpen,
  onClose,
  bookingId,
  patientName,
  hospitalName,
  ambulanceType,
  estimatedFare,
  finalFare,
  driverStatus,
  driverName,
  driverPhone,
  driverId,
  vendorName,
  vendorContact,
  vehicleNumber,
  pickupAddress,
  destinationAddress,
  bookingType,
  direction,
  onTrackBooking,
  matchingStatus,
  contactPhone,
  contactPhone_country_code,
  contactEmail,
  paymentStatus,
  paymentId,
  message,
  scheduledDateTime,
  scheduledDate,
  scheduledTime,
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const [driverAssignmentStatus, setDriverAssignmentStatus] = useState(
    driverStatus || "Waiting for driver assignment..."
  );
  const [currentMatchingStatus, setCurrentMatchingStatus] = useState(matchingStatus || 'searching');
  const [currentDriverName, setCurrentDriverName] = useState(driverName);
  const [currentDriverPhone, setCurrentDriverPhone] = useState(driverPhone);
  const [currentVendorName, setCurrentVendorName] = useState(vendorName);
  const [currentVendorContact, setCurrentVendorContact] = useState(vendorContact);
  const [currentVehicleNumber, setCurrentVehicleNumber] = useState(vehicleNumber);
  const [showDetails, setShowDetails] = useState(false);

  // Check if user is a hospital user or if we're on a hospital route
  const isHospitalContext = () => {
    const hospitalRoles = ['HOSPITAL_USER', 'HOSPITAL_ADMIN', 'hospital'];
    const isHospitalRole = user?.role && hospitalRoles.includes(user.role);
    const isHospitalRoute = location.pathname.includes('/hospital');
    return isHospitalRole || isHospitalRoute;
  };

  // Check if user is a fleet user
  const isFleetContext = () => {
    const fleetRoles = ['TENANT_ADMIN', 'TENANT_USER', 'fleet_operator', 'Fleet'];
    return user?.role && fleetRoles.includes(user.role);
  };

  // Update state when props change
  useEffect(() => {
    setDriverAssignmentStatus(driverStatus || "Waiting for driver assignment...");
    // If matchingStatus is 'assigned' but we don't have driver info, keep it as 'assigned' but polling will fetch details
    // If we have driver info, set to 'assigned', otherwise use the provided status or default to 'searching'
    const initialMatchingStatus = matchingStatus || 
                                 ((driverName && driverName !== 'N/A' && driverName.trim() !== '') || 
                                  (driverPhone && driverPhone !== 'N/A' && driverPhone.trim() !== '')) ? 'assigned' : 'searching';
    setCurrentMatchingStatus(initialMatchingStatus);
    
    // Handle driver name - preserve if valid, otherwise set to empty
    const normalizedDriverName = driverName && driverName !== 'N/A' && String(driverName).trim() !== '' 
      ? String(driverName).trim() 
      : '';
    if (normalizedDriverName) {
      setCurrentDriverName(normalizedDriverName);
    } else if (driverName === undefined || driverName === null) {
      // Only clear if prop is explicitly undefined/null (not provided)
      if (!currentDriverName) {
        setCurrentDriverName('');
      }
    } else {
      // If prop is provided but invalid, set to empty
      setCurrentDriverName('');
    }
    
    // Handle driver phone - preserve if valid, otherwise set to empty
    const normalizedDriverPhone = driverPhone && driverPhone !== 'N/A' && String(driverPhone).trim() !== '' 
      ? String(driverPhone).trim() 
      : '';
    if (normalizedDriverPhone) {
      setCurrentDriverPhone(normalizedDriverPhone);
    } else if (driverPhone === undefined || driverPhone === null) {
      // Only clear if prop is explicitly undefined/null (not provided)
      if (!currentDriverPhone) {
        setCurrentDriverPhone('');
      }
    } else {
      // If prop is provided but invalid, set to empty
      setCurrentDriverPhone('');
    }
    
    // Handle vendor name - always update from props if provided
    const normalizedVendorName = vendorName && vendorName !== 'N/A' && String(vendorName).trim() !== '' 
      ? String(vendorName).trim() 
      : '';
    
    // Always set vendor name from props if it's valid, otherwise keep current value
    if (normalizedVendorName) {
      setCurrentVendorName(normalizedVendorName);
    } else if (vendorName === undefined || vendorName === null) {
      // Only clear if prop is explicitly undefined/null (not provided)
      // Don't clear if it's empty string or 'N/A' - keep current value
    } else {
      // If prop is provided but invalid, set to empty
      setCurrentVendorName('');
    }
    
    // Handle vendor contact - always update from props if provided
    const normalizedVendorContact = vendorContact && vendorContact !== 'N/A' && String(vendorContact).trim() !== '' 
      ? String(vendorContact).trim() 
      : '';
    
    // Always set vendor contact from props if it's valid, otherwise keep current value
    if (normalizedVendorContact) {
      setCurrentVendorContact(normalizedVendorContact);
    } else if (vendorContact === undefined || vendorContact === null) {
      // Only clear if prop is explicitly undefined/null (not provided)
      // Don't clear if it's empty string or 'N/A' - keep current value
    } else {
      // If prop is provided but invalid, set to empty
      setCurrentVendorContact('');
    }
    
    setCurrentVehicleNumber(vehicleNumber || '');
    
    // Log initial state for debugging
    console.log('✅ [SuccessModal] Props updated:', {
      driverName,
      driverPhone,
      vendorName,
      vendorContact,
      normalizedVendorName,
      normalizedVendorContact,
      currentVendorName,
      currentVendorContact,
      vehicleNumber,
      matchingStatus,
      willSetVendorName: !!normalizedVendorName,
      willSetVendorContact: !!normalizedVendorContact
    });
  }, [driverStatus, matchingStatus, driverName, driverPhone, vendorName, vendorContact, vehicleNumber]);

  // Poll for driver assignment updates when modal is open
  // For online payments, continue polling to get driver details even if status shows assigned
  useEffect(() => {
    if (!isOpen || !bookingId) {
      return;
    }

    const pollForDriverAssignment = async () => {
      try {
        let bookingResponse;
        const isHospital = isHospitalContext();
        const isFleet = isFleetContext();
        
        if (isHospital) {
          bookingResponse = await hospitalService.getBookingDetails(bookingId);
        } else if (isFleet) {
          console.log('🚛 [SuccessModal] Using fleet API for booking details');
          bookingResponse = await authService.fleetGetBookingDetails(bookingId);
        } else {
          bookingResponse = await bookingService.getBookingById(bookingId);
        }

        if (bookingResponse?.success && bookingResponse?.data) {
          // Handle different response structures - fleet API might nest booking under 'booking' key
          const booking = bookingResponse.data.booking || bookingResponse.data;
          console.log('📋 [SuccessModal] Booking data structure:', {
            hasBookingKey: !!bookingResponse.data.booking,
            bookingKeys: Object.keys(booking || {}),
            driverMatching: booking?.driver_matching,
            driver: booking?.driver,
            vehicle: booking?.vehicle
          });
          
          const driverMatching = booking.driver_matching || {};
          const assignedDriver = driverMatching?.assigned_driver || booking.driver || booking.vehicle?.assigned_driver;
          const vehicle = booking.vehicle;
          
          // Try multiple locations for operator/vendor information - check all possible locations
          // This is important for fleet users where vendor info might be in different places
          const operator = assignedDriver?.operator || 
                          vehicle?.operator || 
                          booking.operator ||
                          booking.fleet ||
                          booking.fleet_operator ||
                          vehicle?.fleet ||
                          vehicle?.fleet_operator ||
                          assignedDriver?.fleet ||
                          assignedDriver?.fleet_operator ||
                          booking.vehicle?.fleet ||
                          booking.vehicle?.fleet_operator ||
                          booking.vehicle?.operator ||
                          // Also check direct fleet/operator fields in booking
                          (booking.fleet_id ? booking : null) ||
                          (booking.operator_id ? booking : null);

          // Check if driver is assigned (either newly assigned or already assigned)
          // For fleet users, also check booking status and driver_matching status
          const isDriverAssigned = driverMatching?.success || 
                                  booking.driver || 
                                  assignedDriver ||
                                  booking.status === 'ASSIGNED' ||
                                  booking.booking_status === 'ASSIGNED' ||
                                  booking.driver_matching?.status === 'assigned' ||
                                  booking.driver_matching?.assigned_driver;
          
          // If driver is assigned according to status but we don't have driver object yet, log for debugging
          if (isDriverAssigned && !assignedDriver && !booking.driver) {
            console.log('⚠️ [SuccessModal] Driver marked as assigned but driver object not found yet. Will continue polling.');
          }
          
          // Always extract vendor information (even if driver not assigned yet) - important for cash payments
          // Extract vendor information from multiple possible locations
          const extractedVendorName = operator?.name || 
                                    operator?.operator_name || 
                                    operator?.fleet_name ||
                                    operator?.company_name ||
                                    operator?.fleet?.name ||
                                    vehicle?.operator_name || 
                                    vehicle?.operator?.name ||
                                    vehicle?.fleet_name ||
                                    vehicle?.fleet?.name ||
                                    booking.operator?.name ||
                                    booking.operator?.operator_name ||
                                    booking.operator?.fleet_name ||
                                    booking.fleet?.name ||
                                    booking.fleet_name ||
                                    booking.fleet_operator?.name ||
                                    booking.fleet_operator?.operator_name ||
                                    booking.vehicle?.operator?.name ||
                                    booking.vehicle?.operator_name ||
                                    booking.vehicle?.fleet_name ||
                                    booking.vehicle?.fleet?.name ||
                                    booking.vehicle?.fleet_operator?.name ||
                                    '';
          
          const extractedVendorContact = operator?.contact_info || 
                                        operator?.phone || 
                                        operator?.contact_phone ||
                                        operator?.mobile ||
                                        operator?.mobile_number ||
                                        operator?.contact_number ||
                                        operator?.fleet?.phone ||
                                        operator?.fleet?.contact_phone ||
                                        vehicle?.operator?.phone ||
                                        vehicle?.operator?.contact_phone ||
                                        vehicle?.operator?.contact_info ||
                                        vehicle?.fleet?.phone ||
                                        vehicle?.fleet?.contact_phone ||
                                        vehicle?.fleet_operator?.phone ||
                                        vehicle?.fleet_operator?.contact_phone ||
                                        booking.operator?.contact_info ||
                                        booking.operator?.phone ||
                                        booking.operator?.contact_phone ||
                                        booking.operator?.mobile ||
                                        booking.fleet?.phone ||
                                        booking.fleet?.contact_phone ||
                                        booking.fleet?.mobile ||
                                        booking.fleet_name?.phone ||
                                        booking.fleet_operator?.phone ||
                                        booking.fleet_operator?.contact_phone ||
                                        booking.vehicle?.operator?.phone ||
                                        booking.vehicle?.operator?.contact_phone ||
                                        booking.vehicle?.fleet?.phone ||
                                        booking.vehicle?.fleet_operator?.phone ||
                                        '';
          
          // Debug: Always log vendor information extraction (not just when driver assigned)
          const vendorNameStr = extractedVendorName ? String(extractedVendorName).trim() : '';
          const vendorContactStr = extractedVendorContact ? String(extractedVendorContact).trim() : '';
          
          console.log('🔍 [SuccessModal] Vendor information extraction (always):', {
            extractedVendorName: vendorNameStr || 'Not found',
            extractedVendorContact: vendorContactStr || 'Not found',
            operator: operator,
            operatorPhone: operator?.phone,
            operatorContactInfo: operator?.contact_info,
            operatorContactPhone: operator?.contact_phone,
            vehicle: vehicle,
            vehicleOperator: vehicle?.operator,
            vehicleOperatorPhone: vehicle?.operator?.phone,
            bookingOperator: booking.operator,
            bookingOperatorPhone: booking.operator?.phone,
            bookingFleet: booking.fleet,
            bookingFleetPhone: booking.fleet?.phone,
            bookingFleetOperator: booking.fleet_operator,
            bookingVehicleOperator: booking.vehicle?.operator,
            bookingVehicleFleet: booking.vehicle?.fleet,
            bookingVehicleFleetOperator: booking.vehicle?.fleet_operator,
            currentVendorName: currentVendorName,
            currentVendorContact: currentVendorContact,
            paymentStatus: paymentStatus,
            isDriverAssigned: isDriverAssigned,
            fullBooking: booking
          });
          
          // Update vendor information even if driver is not assigned yet (for cash payments)
          const normalizedVendorName = vendorNameStr !== '' ? vendorNameStr : '';
          const normalizedVendorContact = vendorContactStr !== '' ? vendorContactStr : '';
          const hasValidVendorName = normalizedVendorName !== '';
          const hasValidVendorContact = normalizedVendorContact !== '';
          const currentVendorNameStr = currentVendorName ? String(currentVendorName).trim() : '';
          const currentVendorContactStr = currentVendorContact ? String(currentVendorContact).trim() : '';
          
          const hasNewVendorName = hasValidVendorName && normalizedVendorName !== currentVendorNameStr;
          const hasNewVendorContact = hasValidVendorContact && normalizedVendorContact !== currentVendorContactStr;
          const needsVendorNameUpdate = hasValidVendorName && (!currentVendorNameStr || currentVendorNameStr === 'N/A' || currentVendorNameStr === '');
          const needsVendorContactUpdate = hasValidVendorContact && (!currentVendorContactStr || currentVendorContactStr === 'N/A' || currentVendorContactStr === '');
          
          // Always update vendor information if we have valid values and they're missing or different
          if (needsVendorNameUpdate || hasNewVendorName) {
            console.log('🏢 [SuccessModal] Updating vendor name:', {
              extractedVendorName,
              normalizedVendorName,
              currentVendorName,
              willUpdate: true
            });
            setCurrentVendorName(normalizedVendorName);
          }
          
          if (needsVendorContactUpdate || hasNewVendorContact) {
            console.log('📞 [SuccessModal] Updating vendor contact:', {
              extractedVendorContact,
              normalizedVendorContact,
              currentVendorContact,
              willUpdate: true
            });
            setCurrentVendorContact(normalizedVendorContact);
          }
          
          // Update if driver is assigned (either newly assigned or to refresh details)
          if (isDriverAssigned) {
            // Extract driver name from multiple possible locations
            const extractedDriverName = assignedDriver?.driver_name || 
                                      assignedDriver?.name || 
                                      booking.driver?.name || 
                                      booking.driver?.driver_name ||
                                      driverMatching?.assigned_driver?.driver_name || 
                                      driverMatching?.assigned_driver?.name ||
                                      vehicle?.assigned_driver?.name ||
                                      vehicle?.assigned_driver?.driver_name ||
                                      vehicle?.driver?.name ||
                                      booking?.vehicle?.assigned_driver?.name ||
                                      booking?.vehicle?.driver?.name ||
                                      'N/A';
            
            // Extract driver phone from multiple possible locations - check all possible field names
            // Try all possible nested structures and field name variations
            const extractedDriverPhone = assignedDriver?.phone || 
                                       assignedDriver?.contact_phone || 
                                       assignedDriver?.phone_number ||
                                       assignedDriver?.mobile ||
                                       assignedDriver?.mobile_number ||
                                       assignedDriver?.contact_number ||
                                       assignedDriver?.driver_phone ||
                                       assignedDriver?.contact ||
                                       booking.driver?.phone ||
                                       booking.driver?.contact_phone ||
                                       booking.driver?.phone_number ||
                                       booking.driver?.mobile ||
                                       booking.driver?.mobile_number ||
                                       booking.driver?.contact_number ||
                                       booking.driver?.driver_phone ||
                                       booking.driver?.contact ||
                                       driverMatching?.assigned_driver?.phone ||
                                       driverMatching?.assigned_driver?.contact_phone ||
                                       driverMatching?.assigned_driver?.phone_number ||
                                       driverMatching?.assigned_driver?.mobile ||
                                       driverMatching?.assigned_driver?.mobile_number ||
                                       driverMatching?.assigned_driver?.contact_number ||
                                       vehicle?.assigned_driver?.phone ||
                                       vehicle?.assigned_driver?.contact_phone ||
                                       vehicle?.driver?.phone ||
                                       vehicle?.driver?.contact_phone ||
                                       booking?.vehicle?.assigned_driver?.phone ||
                                       booking?.vehicle?.assigned_driver?.contact_phone ||
                                       booking?.vehicle?.driver?.phone ||
                                       booking?.vehicle?.driver?.contact_phone ||
                                       '';
            
            // Debug: Log driver information extraction
            console.log('👤 [SuccessModal] Driver information extraction:', {
              isDriverAssigned,
              extractedDriverName: extractedDriverName !== 'N/A' ? extractedDriverName : 'Not found',
              extractedDriverPhone: extractedDriverPhone || 'Not found',
              assignedDriver: assignedDriver ? { name: assignedDriver.name || assignedDriver.driver_name, phone: assignedDriver.phone || assignedDriver.contact_phone } : 'Not found',
              bookingDriver: booking.driver ? { name: booking.driver.name, phone: booking.driver.phone } : 'Not found',
              vehicleDriver: vehicle?.driver ? { name: vehicle.driver.name, phone: vehicle.driver.phone } : 'Not found',
              driverMatching: driverMatching?.assigned_driver ? { name: driverMatching.assigned_driver.driver_name || driverMatching.assigned_driver.name, phone: driverMatching.assigned_driver.phone } : 'Not found'
            });
            
            // Debug: Log all possible phone locations
            if (isDriverAssigned && !extractedDriverPhone) {
              console.log('⚠️ [SuccessModal] Driver assigned but phone not found. Checking locations:', {
                assignedDriver: assignedDriver,
                bookingDriver: booking.driver,
                driverMatchingAssignedDriver: driverMatching?.assigned_driver,
                vehicleAssignedDriver: vehicle?.assigned_driver,
                vehicleDriver: vehicle?.driver,
                fullBooking: booking
              });
            }
            
            // Vendor information already extracted above (before driver check)
            // Use the already extracted values
            
            // Extract vehicle number
            const extractedVehicleNumber = vehicle?.vehicle_number || 
                                         vehicle?.registration_number || 
                                         vehicle?.ambulance_number ||
                                         booking.vehicle?.vehicle_number ||
                                         booking.vehicle?.registration_number ||
                                         'N/A';
            
            // Normalize phone number - remove any whitespace and ensure it's a valid string
            const normalizedPhone = extractedDriverPhone ? String(extractedDriverPhone).trim() : '';
            const hasValidPhone = normalizedPhone && normalizedPhone.length > 0 && normalizedPhone !== 'N/A';
            
            // Update state if driver was just assigned, if we have new phone number, or to ensure we get driver details
            const hasNewPhone = hasValidPhone && normalizedPhone !== currentDriverPhone;
            const needsPhoneUpdate = hasValidPhone && (!currentDriverPhone || currentDriverPhone === 'N/A' || currentDriverPhone.trim() === '');
            const hasNewDriverName = extractedDriverName && extractedDriverName !== currentDriverName && extractedDriverName !== 'N/A';
            
            // Vendor information normalization already done above
            
            // Always update if driver is assigned and we have any driver information
            const hasDriverInfo = (extractedDriverName && extractedDriverName !== 'N/A' && extractedDriverName.trim() !== '') ||
                                 hasValidPhone;
            
            // Always update driver information if driver is assigned and we have valid info
            // This ensures driver details are displayed for both online and cash payments
            // Also update if we have driver info (even if assignment status check isn't perfect)
            if (isDriverAssigned && hasDriverInfo) {
              console.log('🔄 [SuccessModal] Updating driver information:', {
                isDriverAssigned,
                hasDriverInfo,
                extractedDriverName: extractedDriverName !== 'N/A' ? extractedDriverName : 'Not found',
                extractedDriverPhone: extractedDriverPhone || 'Not found',
                currentDriverName: currentDriverName || 'Empty',
                currentDriverPhone: currentDriverPhone || 'Empty',
                currentMatchingStatus,
                paymentStatus,
                willUpdate: true
              });
              setCurrentMatchingStatus('assigned');
              setDriverAssignmentStatus(driverMatching?.message || 'Driver assigned');
              
              // Always update driver name if we have a valid value
              if (extractedDriverName && extractedDriverName !== 'N/A' && extractedDriverName.trim() !== '') {
                setCurrentDriverName(extractedDriverName.trim());
              } else if (!currentDriverName || currentDriverName === 'N/A' || currentDriverName.trim() === '') {
                // Only set to empty if current is also empty/N/A
                setCurrentDriverName('');
              }
              
              // Always update phone if we have a valid value, otherwise keep current value (don't overwrite with empty)
              if (hasValidPhone) {
                setCurrentDriverPhone(normalizedPhone);
              } else if (!currentDriverPhone || currentDriverPhone === 'N/A' || currentDriverPhone.trim() === '') {
                // Only set to empty if current is also empty/N/A
                setCurrentDriverPhone('');
              }
              // Always update vendor information if we have valid values
              // Use the already normalized values from above
              if (normalizedVendorName) {
                console.log('🏢 [SuccessModal] Updating vendor name:', {
                  extractedVendorName,
                  normalizedVendorName,
                  currentVendorName,
                  willUpdate: true
                });
                setCurrentVendorName(normalizedVendorName);
              } else {
                // Only clear if current is also empty/N/A and we still don't have a value
                if ((!currentVendorNameStr || currentVendorNameStr === 'N/A') && !normalizedVendorName) {
                  // Don't clear if we already have a value from props
                  if (!currentVendorName) {
                    setCurrentVendorName('');
                  }
                }
              }
              
              // Always update if we found a valid vendor contact
              if (normalizedVendorContact) {
                console.log('📞 [SuccessModal] Updating vendor contact:', {
                  extractedVendorContact,
                  normalizedVendorContact,
                  currentVendorContact,
                  willUpdate: true
                });
                setCurrentVendorContact(normalizedVendorContact);
              } else {
                // Only clear if current is also empty/N/A and we still don't have a value
                if ((!currentVendorContactStr || currentVendorContactStr === 'N/A') && !normalizedVendorContact) {
                  console.log('📞 [SuccessModal] Vendor contact not found, keeping current:', {
                    extractedVendorContact,
                    normalizedVendorContact,
                    currentVendorContact
                  });
                  // Don't clear if we already have a value from props
                  if (!currentVendorContact) {
                    setCurrentVendorContact('');
                  }
                }
              }
              
              setCurrentVehicleNumber(extractedVehicleNumber);
              
              console.log('✅ [SuccessModal] Driver details updated:', {
                driverName: extractedDriverName,
                driverPhone: normalizedPhone || 'Not found',
                hasValidPhone: hasValidPhone,
                currentDriverPhone: currentDriverPhone,
                vendorName: normalizedVendorName,
                vendorContact: normalizedVendorContact,
                currentVendorName: currentVendorName,
                currentVendorContact: currentVendorContact,
                vehicleNumber: extractedVehicleNumber,
                paymentStatus: paymentStatus,
                operator: operator,
                vehicle: vehicle,
                assignedDriver: assignedDriver,
                bookingDriver: booking.driver,
                driverMatching: driverMatching,
                fullBooking: booking
              });
            } else {
              // Log when we don't update to help debug
              console.log('ℹ️ [SuccessModal] Driver details not updated:', {
                currentMatchingStatus,
                hasNewPhone,
                needsPhoneUpdate,
                extractedDriverPhone: normalizedPhone,
                currentDriverPhone,
                extractedDriverName,
                currentDriverName
              });
            }
          } else {
            // Driver not assigned yet, but we still want to update vendor info if available
            console.log('ℹ️ [SuccessModal] Driver not assigned yet, but vendor info extracted:', {
              vendorName: normalizedVendorName,
              vendorContact: normalizedVendorContact,
              currentVendorName: currentVendorName,
              currentVendorContact: currentVendorContact
            });
          }
        }
      } catch (error) {
        console.error('❌ [SuccessModal] Error polling for driver assignment:', error);
      }
    };

    // Poll every 3 seconds (continue polling even if assigned to get latest details, especially for online payments)
    // Continue polling if driver is assigned but we don't have driver name, phone number, or vendor information yet
    const needsDriverName = !currentDriverName || currentDriverName === 'N/A' || currentDriverName.trim() === '';
    const needsDriverPhone = !currentDriverPhone || currentDriverPhone === 'N/A' || currentDriverPhone.trim() === '';
    const needsVendorInfo = !currentVendorName || currentVendorName === 'N/A' || !currentVendorContact || currentVendorContact === 'N/A';
    // For online payments, always continue polling if driver is assigned but we don't have driver info yet
    // Also continue polling if matchingStatus is 'assigned' but we don't have driver info (important for online payments)
    // Always poll if we're missing driver information (critical for online payments in Fleet Dashboard)
    const shouldContinuePolling = (currentMatchingStatus === 'assigned' && (needsDriverName || needsDriverPhone || needsVendorInfo)) ||
                                  (paymentStatus === 'success' && (needsDriverName || needsDriverPhone)) ||
                                  (paymentStatus === 'success' && currentMatchingStatus === 'assigned' && (needsDriverName || needsDriverPhone));
    const pollInterval = shouldContinuePolling ? 3000 : 5000; // Poll more frequently if we need missing information
    
    console.log('🔄 [SuccessModal] Polling configuration:', {
      currentMatchingStatus,
      paymentStatus,
      needsDriverName,
      needsDriverPhone,
      needsVendorInfo,
      shouldContinuePolling,
      pollInterval,
      currentDriverName: currentDriverName || 'Empty',
      currentDriverPhone: currentDriverPhone || 'Empty'
    });
    
    const intervalId = setInterval(pollForDriverAssignment, pollInterval);
    
    // Initial poll
    pollForDriverAssignment();

    return () => {
      clearInterval(intervalId);
    };
  }, [isOpen, bookingId, currentMatchingStatus, currentDriverPhone, currentDriverName, currentVendorName, currentVendorContact, paymentStatus]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Use requestAnimationFrame to prevent ResizeObserver issues
      requestAnimationFrame(() => {
        document.body.style.overflow = "hidden";
      });
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      // Use requestAnimationFrame to prevent ResizeObserver issues
      requestAnimationFrame(() => {
        document.body.style.overflow = "unset";
      });
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg max-w-md w-full mx-auto shadow-xl overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="bg-green-50 p-4 rounded-t-lg border-b border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-green-100 p-1.5 rounded-full">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div className="ml-2">
                <h3 className="text-base font-semibold text-green-900">
                  Ambulance Booked Successfully!
                </h3>
                <p className="text-xs text-green-700">
                  Your emergency request has been confirmed
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-green-400 hover:text-green-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Payment Success Message */}
          {paymentStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm font-semibold text-green-900">
                    Payment Confirmed!
                  </p>
                  <p className="text-xs text-green-700">
                    {message || 'Your booking is confirmed and payment has been processed successfully.'}
                  </p>
                  {paymentId && (
                    <p className="text-xs text-green-600 mt-1 font-mono">
                      Payment ID: {paymentId}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Booking ID */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-center">
              <h4 className="text-sm font-medium text-blue-900 mb-1">
                Booking ID
              </h4>
              <p className="text-base font-bold text-blue-800 font-mono tracking-wider break-all">
                {bookingId || "N/A"}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Save this ID for tracking your ambulance
              </p>
              {bookingId && (
                <p className="text-xs text-green-600 mt-1 font-semibold">
                  ✓ Booking Created Successfully
                </p>
              )}
            </div>
          </div>

          {/* Essential Info */}
          <div className="grid grid-cols-2 gap-3">
            {patientName && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Patient</p>
                <p className="text-sm font-medium text-gray-900">
                  {patientName}
                </p>
              </div>
            )}
            {estimatedFare && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Estimated Fare</p>
                <p className="text-lg font-bold text-gray-900">
                  ₹{estimatedFare}
                </p>
              </div>
            )}
          </div>

          {/* Trip Route */}
          {pickupAddress && destinationAddress && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
              <h4 className="text-xs font-medium text-gray-900 mb-2 flex items-center">
                <Navigation className="w-3 h-3 mr-1.5 text-blue-600" />
                Trip Route
              </h4>
              <div className="space-y-1.5">
                {/* Pickup */}
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-4 h-4 bg-green-100 rounded-full flex items-center justify-center mr-2 mt-0.5">
                    <div className="w-1.5 h-1.5 bg-green-600 rounded-full"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-medium text-green-700 uppercase tracking-wide">
                      Pickup
                    </p>
                    <p className="text-xs text-gray-900 mt-0.5 break-words leading-tight">
                      {pickupAddress}
                    </p>
                  </div>
                </div>

                {/* Route Line */}
                <div className="flex items-center ml-2">
                  <div className="w-px h-3 bg-gray-300 ml-1"></div>
                </div>

                {/* Destination */}
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-4 h-4 bg-red-100 rounded-full flex items-center justify-center mr-2 mt-0.5">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-medium text-red-700 uppercase tracking-wide">
                      Destination
                    </p>
                    <p className="text-xs text-gray-900 mt-0.5 break-words leading-tight">
                      {destinationAddress}
                    </p>
                  </div>
                </div>

                {/* Additional Trip Info */}
                {(direction || bookingType) && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <div className="flex flex-wrap gap-1.5">
                      {direction && (
                        <div className="flex items-center bg-blue-100 px-1.5 py-0.5 rounded">
                          <Navigation className="w-2.5 h-2.5 text-blue-600 mr-1" />
                          <span className="text-[10px] text-blue-700 font-medium">
                            {direction}
                          </span>
                        </div>
                      )}
                      {bookingType && (
                        <div className="flex items-center bg-purple-100 px-1.5 py-0.5 rounded">
                          <Clock className="w-2.5 h-2.5 text-purple-600 mr-1" />
                          <span className="text-[10px] text-purple-700 font-medium capitalize">
                            {bookingType}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Scheduled Date/Time Display */}
          {(bookingType === "SCHEDULED" || bookingType === "scheduled") && (scheduledDateTime || (scheduledDate && scheduledTime)) && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-purple-600" />
                <h4 className="text-sm font-medium text-purple-900">
                  Scheduled Date & Time
                </h4>
              </div>
              <p className="text-sm font-semibold text-purple-800">
                {scheduledDateTime
                  ? new Date(scheduledDateTime).toLocaleString('en-IN', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })
                  : scheduledDate && scheduledTime
                    ? new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString('en-IN', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      })
                    : 'N/A'}
              </p>
            </div>
          )}

          {/* Driver Status */}
          <div
            className={`border rounded-lg p-3 ${currentMatchingStatus === "searching" || currentMatchingStatus === "reassigning"
                ? "bg-blue-50 border-blue-200"
                : currentMatchingStatus === "assigned"
                  ? "bg-green-50 border-green-200"
                  : currentMatchingStatus === "failed" || currentMatchingStatus === "timeout"
                    ? "bg-red-50 border-red-200"
                    : "bg-yellow-50 border-yellow-200"
              }`}
          >
            <div className="flex items-center">
              {currentMatchingStatus === "searching" ||
                currentMatchingStatus === "reassigning" ? (
                <Loader2 className="w-4 h-4 text-blue-600 mr-2 animate-spin" />
              ) : currentMatchingStatus === "assigned" ? (
                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
              ) : currentMatchingStatus === "failed" ||
                currentMatchingStatus === "timeout" ? (
                <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
              ) : (
                <Users className="w-4 h-4 text-yellow-600 mr-2" />
              )}
              <div className="flex-1">
                {/* Show driver details when available (for both online and cash payments), otherwise show status message */}
                {/* Always show driver name and contact number if available, regardless of exact status */}
                {(() => {
                  const hasValidDriverName = currentDriverName && currentDriverName !== 'N/A' && currentDriverName.trim() !== '';
                  const hasValidDriverPhone = currentDriverPhone && currentDriverPhone !== 'N/A' && currentDriverPhone.trim() !== '';
                  const hasAnyDriverInfo = hasValidDriverName || hasValidDriverPhone;
                  const isScheduledBooking = bookingType === "SCHEDULED" || bookingType === "scheduled";
                  
                  // Debug logging for display condition
                  if (process.env.NODE_ENV === 'development') {
                    console.log('🖥️ [SuccessModal] Display condition check:', {
                      currentMatchingStatus,
                      currentDriverName: currentDriverName || 'Empty',
                      currentDriverPhone: currentDriverPhone || 'Empty',
                      hasValidDriverName,
                      hasValidDriverPhone,
                      hasAnyDriverInfo,
                      paymentStatus,
                      isScheduledBooking,
                      willShowDriverDetails: hasAnyDriverInfo
                    });
                  }
                  
                  return hasAnyDriverInfo;
                })() ? (
                      <div className="space-y-2">
                        {/* Driver Name - Always show if available */}
                        {currentDriverName && currentDriverName !== 'N/A' && currentDriverName.trim() !== '' && (
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-green-600" />
                            <div>
                              <p className="text-xs text-gray-500">Driver Name</p>
                              <p className="text-sm font-medium text-gray-900">{currentDriverName}</p>
                            </div>
                          </div>
                        )}
                        {/* Driver Contact Number - Always show if available */}
                        {currentDriverPhone && currentDriverPhone !== 'N/A' && currentDriverPhone.trim() !== '' ? (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-green-600" />
                            <div>
                              <p className="text-xs text-gray-500">Driver Contact</p>
                              <p className="text-sm font-medium text-gray-900">{currentDriverPhone}</p>
                            </div>
                          </div>
                        ) : currentDriverName && currentDriverName !== 'N/A' && currentDriverName.trim() !== '' ? (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="text-xs text-gray-500">Driver Contact</p>
                              <p className="text-sm font-medium text-gray-500">Not available</p>
                            </div>
                          </div>
                        ) : null}
                        {currentVehicleNumber && currentVehicleNumber !== 'N/A' && (
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-green-600" />
                            <div>
                              <p className="text-xs text-gray-500">Vehicle Number</p>
                              <p className="text-sm font-medium text-gray-900">{currentVehicleNumber}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p
                        className={`text-sm font-medium ${currentMatchingStatus === "searching" ||
                            currentMatchingStatus === "reassigning"
                            ? "text-blue-800"
                            : currentMatchingStatus === "assigned"
                              ? "text-green-800"
                              : currentMatchingStatus === "failed" ||
                                currentMatchingStatus === "timeout"
                                ? "text-red-800"
                                : "text-yellow-800"
                          }`}
                      >
                        {(() => {
                          const isScheduledBooking = bookingType === "SCHEDULED" || bookingType === "scheduled";
                          if (isScheduledBooking) {
                            return "Booking created successfully! Driver will be assigned closer to the scheduled time.";
                          }
                          return currentMatchingStatus === "assigned" 
                            ? "Driver has been successfully assigned!" 
                            : driverAssignmentStatus;
                        })()}
                      </p>
                    )}
                    
                    {/* Helper text - only show when NOT displaying driver details */}
                    {!(currentMatchingStatus === "assigned" && (currentDriverName && currentDriverName !== 'N/A' && currentDriverName.trim() !== '' || currentDriverPhone && currentDriverPhone !== 'N/A' && currentDriverPhone.trim() !== '')) && (
                      <>
                        <p className={`text-xs mt-2 ${currentMatchingStatus === 'searching' || currentMatchingStatus === 'reassigning'
                            ? 'text-blue-700'
                            : currentMatchingStatus === 'assigned'
                              ? 'text-green-700'
                              : currentMatchingStatus === 'failed' || currentMatchingStatus === 'timeout'
                                ? 'text-red-700'
                                : 'text-yellow-700'
                          }`}>
                          {(() => {
                            const isScheduledBooking = bookingType === "SCHEDULED" || bookingType === "scheduled";
                            if (isScheduledBooking) {
                              return 'Your booking has been confirmed. A driver will be assigned closer to the scheduled date and time.';
                            }
                            if (currentMatchingStatus === 'searching') {
                              return 'Finding the nearest available driver...';
                            }
                            if (currentMatchingStatus === 'reassigning') {
                              return 'Previous driver declined. Finding alternative driver...';
                            }
                            if (currentMatchingStatus === 'assigned') {
                              return 'Driver has been successfully assigned!';
                            }
                            if (currentMatchingStatus === 'failed') {
                              return 'Driver assignment failed. Please contact support.';
                            }
                            if (currentMatchingStatus === 'timeout') {
                              return 'Driver assignment timeout. Please contact support.';
                            }
                            return 'We are searching for the best available driver';
                          })()}
                        </p>
                        {currentMatchingStatus === 'reassigning' && (
                          <p className="text-xs text-blue-600 mt-1">
                            🔄 Automatically searching for next available driver...
                          </p>
                        )}
                      </>
                    )}
              </div>
            </div>
          </div>

          {/* Accordion for Additional Details */}
          <div className="border border-gray-200 rounded-lg">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm font-medium text-gray-700">
                Additional Details
              </span>
              {showDetails ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </button>

            {showDetails && (
              <div className="border-t border-gray-200 p-3 space-y-3">
                {/* Vendor Name */}
                {(() => {
                  const vendorNameValue = currentVendorName;
                  const vendorNameStr = vendorNameValue ? String(vendorNameValue).trim() : '';
                  const isValidVendorName = vendorNameStr !== '' && 
                                           vendorNameValue !== 'N/A' && 
                                           vendorNameValue !== undefined &&
                                           vendorNameValue !== null;
                  
                  // Debug log
                  if (process.env.NODE_ENV === 'development') {
                    console.log('🔍 [SuccessModal] Vendor Name Display Check:', {
                      vendorNameValue,
                      vendorNameStr,
                      isValidVendorName,
                      type: typeof vendorNameValue
                    });
                  }
                  
                  if (isValidVendorName) {
                    return (
                      <div className="flex items-center">
                        <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                          <Building2 className="w-3 h-3 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Vendor/Fleet</p>
                          <p className="text-sm font-medium text-gray-900">
                            {vendorNameStr}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Ambulance Type */}
                {ambulanceType && (
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                      <svg
                        className="w-3 h-3 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Ambulance Type</p>
                      <p className="text-sm font-medium text-gray-900">
                        {ambulanceType}
                      </p>
                    </div>
                  </div>
                )}

                {/* Contact Information */}
                {(contactPhone || contactEmail) && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Contact Information
                    </h4>
                    <div className="space-y-2">
                      {contactPhone && (
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 text-blue-600 mr-2" />
                          <div>
                            <p className="text-xs text-gray-500">Phone</p>
                            <p className="text-sm font-medium text-gray-900">
                              {contactPhone_country_code
                                ? `${contactPhone_country_code} ${contactPhone}`
                                : contactPhone}
                            </p>
                          </div>
                        </div>
                      )}
                      {contactEmail && (
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 text-blue-600 mr-2" />
                          <div>
                            <p className="text-xs text-gray-500">Email</p>
                            <p className="text-sm font-medium text-gray-900">
                              {contactEmail}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Vendor Contact Number - Show if we have any valid contact value */}
                {(() => {
                  const vendorContactValue = currentVendorContact;
                  const vendorContactStr = vendorContactValue ? String(vendorContactValue).trim() : '';
                  const isValidContact = vendorContactStr !== '' && 
                                        vendorContactValue !== 'N/A' && 
                                        vendorContactValue !== undefined &&
                                        vendorContactValue !== null;
                  
                  // Debug log
                  if (process.env.NODE_ENV === 'development') {
                    console.log('🔍 [SuccessModal] Vendor Contact Display Check:', {
                      vendorContactValue,
                      vendorContactStr,
                      isValidContact,
                      type: typeof vendorContactValue,
                      length: vendorContactStr.length
                    });
                  }
                  
                  if (isValidContact) {
                    return (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 text-blue-600 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-blue-800">
                              Vendor Contact Number
                            </p>
                            <p className="text-lg font-bold text-blue-900">{vendorContactStr}</p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 rounded-b-lg">
          <div className="flex justify-between items-center">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => {
                if (onTrackBooking) {
                  onTrackBooking();
                } else {
                  // Fallback: navigate to appropriate tracking page based on context
                  onClose();
                  if (bookingId) {
                    setTimeout(() => {
                      // For hospital users or hospital routes, redirect to hospital dashboard
                      if (isHospitalContext()) {
                        window.location.href = `/hospital?bookingId=${bookingId}`;
                      } else {
                        // For other users, redirect to track-booking page
                        window.location.href = `/track-booking?bookingId=${bookingId}`;
                      }
                    }, 100);
                  } else {
                    // If no bookingId, navigate based on context
                    setTimeout(() => {
                      if (isHospitalContext()) {
                        window.location.href = `/hospital`;
                      } else {
                        window.location.href = `/track-booking`;
                      }
                    }, 100);
                  }
                }
              }}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Navigation className="w-4 h-4" />
              Track Booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessModal;
